import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { api, formatBRL } from "../lib/api";
import { toast } from "sonner";
import {
  LifeBuoy, Loader2, RefreshCcw, AlertTriangle, CheckCircle2,
  Handshake, ArrowRightLeft, X, MessageSquare,
} from "lucide-react";
import NewInstallmentContractModal from "../components/NewInstallmentContractModal";
import RecuperacaoPerformance from "../components/RecuperacaoPerformance";
import SendWhatsAppModal from "../components/SendWhatsAppModal";
import { useAuth } from "../lib/auth";

const EXIT_REASONS = [
  "Recuperado",
  "Renegociado",
  "Judicial",
  "Prejuízo",
  "Cancelado",
  "Reativado para carteira operacional",
];

function MetricCard({ label, value, accent = "gold", hint, testId }) {
  const color = {
    gold: "text-[#D4AF37] border-[#D4AF37]/30",
    amber: "text-amber-300 border-amber-500/30",
    cyan: "text-cyan-300 border-cyan-500/30",
    emerald: "text-emerald-300 border-emerald-500/30",
    red: "text-red-300 border-red-500/30",
    neutral: "text-[#A3A3A3] border-[#1f1f1f]",
  }[accent] || "text-[#D4AF37] border-[#D4AF37]/30";
  return (
    <div className={`rounded-md border ${color} bg-[#0a0a0a] px-3 py-3`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-[#737373]">{label}</div>
      <div className={`font-mono-tab text-lg mt-1 ${color.split(" ")[0]}`}>{value}</div>
      {hint && <div className="text-[10px] text-[#737373] mt-1">{hint}</div>}
    </div>
  );
}

function TipoBadge({ tipo }) {
  const map = {
    contrato: { label: "Contrato Normal", c: "border-[#D4AF37]/30 text-[#D4AF37] bg-[#0f0f0f]" },
    parcelado: { label: "Parcelado", c: "border-cyan-500/30 text-cyan-300 bg-[#0f0f0f]" },
    acordo: { label: "Acordo", c: "border-fuchsia-500/30 text-fuchsia-300 bg-[#0f0f0f]" },
  };
  const t = map[tipo] || map.contrato;
  return <span className={`badge-base text-[10px] border ${t.c}`}>{t.label}</span>;
}

function ExitModal({ row, onClose, onDone }) {
  const [reason, setReason] = useState("Recuperado");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/recovery/${row.recovery_id}/exit`, { reason, notes: notes || null });
      toast.success(`Movido para fora da recuperação · ${reason}`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao mover");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="exit-recovery-modal">
      <div className="fc-card w-full max-w-md my-8 p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-display text-lg font-semibold">Mover para fora da Recuperação</h3>
            <p className="text-[12px] text-[#A3A3A3] mt-1">{row.client_name} · {row.tipo_legivel}</p>
          </div>
          <button onClick={onClose} className="text-[#737373] hover:text-white"><X size={16}/></button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#737373] block mb-1">Motivo</label>
            <select
              className="fc-input w-full text-[13px]"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="exit-reason-select"
            >
              {EXIT_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#737373] block mb-1">Observações (opcional)</label>
            <textarea
              className="fc-input w-full text-[13px]"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="exit-notes-input"
              placeholder="Detalhes sobre o motivo da saída…"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="fc-btn-ghost text-xs">Cancelar</button>
          <button
            onClick={submit}
            disabled={saving}
            className="fc-btn-primary text-xs flex items-center gap-1.5"
            data-testid="exit-confirm-btn"
          >
            {saving ? <Loader2 size={12} className="animate-spin"/> : <ArrowRightLeft size={12}/>}
            Confirmar saída
          </button>
        </div>
      </div>
    </div>
  );
}

export default function RecuperacaoPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "gestora";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("carteira"); // "carteira" | "performance"
  const [tab, setTab] = useState("ativos");
  const [search, setSearch] = useState("");
  const [exitRow, setExitRow] = useState(null);   // {recovery_id, client_name, tipo_legivel}
  const [negociar, setNegociar] = useState(null); // {client_id, saldo, recovery_id, record_id}
  const [waClient, setWaClient] = useState(null); // {id, full_name}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/recovery/portfolio");
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao carregar carteira de recuperação");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const s = data?.summary || {};
  const itemsAll = tab === "ativos" ? (data?.items_ativos || []) : (data?.items_recuperados || []);
  const items = search.trim()
    ? itemsAll.filter((i) =>
        (i.client_name || "").toLowerCase().includes(search.toLowerCase()) ||
        (i.socio_name || "").toLowerCase().includes(search.toLowerCase()))
    : itemsAll;

  return (
    <Layout>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-amber-300">Carteira problemática</div>
          <h1 className="font-display text-3xl sm:text-[34px] font-bold tracking-tight mt-1" data-testid="recuperacao-title">
            Recuperação
          </h1>
          <p className="text-[#A3A3A3] text-[13px] mt-1.5 max-w-2xl">
            Carteira independente. Contratos com mais de <strong className="text-amber-300">30 dias de atraso</strong> entram
            automaticamente e só saem quando totalmente quitados ou movidos manualmente por um administrador.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="fc-btn-ghost text-xs flex items-center gap-1.5 self-start sm:self-auto"
          data-testid="btn-reload-recuperacao"
        >
          {loading ? <Loader2 size={13} className="animate-spin"/> : <RefreshCcw size={13}/>}
          Atualizar
        </button>
      </div>

      {/* Top-level switcher: Carteira vs Performance */}
      <div className="flex gap-1.5 mb-5 border-b border-[#1f1f1f]">
        <button
          onClick={() => setView("carteira")}
          className={`text-[12.5px] px-4 py-2 -mb-px border-b-2 transition-colors ${
            view === "carteira"
              ? "border-[#D4AF37] text-[#D4AF37]"
              : "border-transparent text-[#737373] hover:text-[#A3A3A3]"
          }`}
          data-testid="view-carteira"
        >
          Carteira
        </button>
        <button
          onClick={() => setView("performance")}
          className={`text-[12.5px] px-4 py-2 -mb-px border-b-2 transition-colors ${
            view === "performance"
              ? "border-[#D4AF37] text-[#D4AF37]"
              : "border-transparent text-[#737373] hover:text-[#A3A3A3]"
          }`}
          data-testid="view-performance"
        >
          Performance
        </button>
      </div>

      {view === "performance" ? (
        <RecuperacaoPerformance />
      ) : (
      <>
      {/* Métricas - 8 cards */}
      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3" data-testid="recuperacao-metrics-row1">
            <MetricCard label="Capital em recuperação" value={formatBRL(s.capital_em_recuperacao)} accent="amber" testId="metric-capital" />
            <MetricCard label="Juros em recuperação" value={formatBRL(s.juros_em_recuperacao)} accent="amber" testId="metric-juros" />
            <MetricCard
              label="Total em recuperação"
              value={formatBRL(s.total_em_recuperacao)}
              accent="amber"
              hint={`${s.contratos_ativos} contrato(s) · ${s.clientes_ativos} cliente(s)`}
              testId="metric-total"
            />
            <MetricCard
              label="Valor recuperado"
              value={formatBRL(s.valor_recuperado)}
              accent="emerald"
              hint={`${s.pct_recuperado}% da carteira`}
              testId="metric-recuperado"
            />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3" data-testid="recuperacao-metrics-row2">
            <MetricCard
              label="Valor ainda em aberto"
              value={formatBRL(s.valor_em_aberto)}
              accent="red"
              hint={`${s.pct_pendente}% pendente`}
              testId="metric-aberto"
            />
            <MetricCard
              label="Dias em recuperação (média)"
              value={`${s.dias_em_recuperacao_avg ?? 0} d`}
              accent="neutral"
              hint="Tempo médio dos ativos"
              testId="metric-dias-rec"
            />
            <MetricCard
              label="Maior atraso"
              value={`${s.maior_atraso ?? 0} d`}
              accent="red"
              hint="Pior caso atual"
              testId="metric-maior-atraso"
            />
            <MetricCard
              label="Ticket médio"
              value={formatBRL(s.ticket_medio)}
              accent="cyan"
              hint="Total carteira ÷ contratos"
              testId="metric-ticket"
            />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="recuperacao-metrics-row3">
            <MetricCard
              label="Contratos ativos"
              value={String(s.contratos_ativos ?? 0)}
              accent="amber"
              testId="metric-contratos-ativos"
            />
            <MetricCard
              label="Contratos recuperados"
              value={String(s.contratos_recuperados ?? 0)}
              accent="emerald"
              hint="Histórico permanente"
              testId="metric-contratos-recuperados"
            />
            <MetricCard
              label="Taxa de recuperação"
              value={`${s.taxa_recuperacao ?? 0}%`}
              accent="emerald"
              hint="Recuperados ÷ (ativos+recuperados)"
              testId="metric-taxa-recuperacao"
            />
            <MetricCard
              label="Inadimplência da recuperação"
              value={`${s.inad_recuperacao_pct ?? 0}%`}
              accent="red"
              hint="Capital em aberto / total"
              testId="metric-inad"
            />
          </div>
        </>
      )}

      <section className="fc-card p-5 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex gap-2">
            <button
              onClick={() => setTab("ativos")}
              className={`text-xs px-3 py-2 rounded-md border ${
                tab === "ativos"
                  ? "border-amber-500/50 text-amber-200 bg-amber-500/10"
                  : "border-[#1f1f1f] text-[#A3A3A3] hover:bg-[#0f0f0f]"
              }`}
              data-testid="tab-ativos"
            >
              <AlertTriangle size={12} className="inline mr-1.5"/>
              Em recuperação ({data?.items_ativos?.length || 0})
            </button>
            <button
              onClick={() => setTab("recuperados")}
              className={`text-xs px-3 py-2 rounded-md border ${
                tab === "recuperados"
                  ? "border-emerald-500/50 text-emerald-200 bg-emerald-500/10"
                  : "border-[#1f1f1f] text-[#A3A3A3] hover:bg-[#0f0f0f]"
              }`}
              data-testid="tab-recuperados"
            >
              <CheckCircle2 size={12} className="inline mr-1.5"/>
              Recuperados / Encerrados ({data?.items_recuperados?.length || 0})
            </button>
          </div>
          <input
            type="text"
            className="fc-input text-[12.5px] sm:w-72"
            placeholder="Buscar cliente ou sócio…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="search-recuperacao"
          />
        </div>

        {loading && !data ? (
          <div className="text-[13px] text-[#737373] flex items-center gap-2">
            <Loader2 size={14} className="animate-spin"/> Carregando…
          </div>
        ) : !data || items.length === 0 ? (
          <div className="text-[13px] text-[#737373] flex items-center gap-2">
            <CheckCircle2 size={14} className="text-emerald-400"/>
            {tab === "ativos"
              ? "Nenhum registro com atraso superior a 30 dias."
              : "Nenhum registro encerrado no histórico ainda."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]" data-testid="recuperacao-table">
              <thead>
                <tr className="text-[10.5px] uppercase tracking-wider text-[#737373] border-b border-[#1f1f1f]">
                  <th className="text-left py-2 px-2">Tipo</th>
                  <th className="text-left py-2 px-2">Cliente</th>
                  <th className="text-left py-2 px-2">Sócio</th>
                  {tab === "ativos" && <th className="text-left py-2 px-2">Prob.</th>}
                  {tab === "ativos" ? (
                    <>
                      <th className="text-right py-2 px-2">Atraso</th>
                      <th className="text-right py-2 px-2">Em rec.</th>
                    </>
                  ) : (
                    <th className="text-left py-2 px-2">Saída</th>
                  )}
                  <th className="text-right py-2 px-2">Capital</th>
                  <th className="text-right py-2 px-2">Juros</th>
                  <th className="text-right py-2 px-2">{tab === "ativos" ? "Saldo" : "Carteira"}</th>
                  <th className="text-right py-2 px-2">Pago</th>
                  <th className="text-left py-2 px-2">Status</th>
                  {tab === "ativos" && <th className="text-right py-2 px-2">Ações</th>}
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.recovery_id} className="border-b border-[#141414] hover:bg-[#0a0a0a]">
                    <td className="py-1.5 px-2"><TipoBadge tipo={it.tipo}/></td>
                    <td className="py-1.5 px-2">
                      {it.client_id ? (
                        <Link to={`/clientes/${it.client_id}`} className="text-[#D4AF37] hover:underline" data-testid={`row-client-${it.recovery_id}`}>
                          {it.client_name || "—"}
                        </Link>
                      ) : (it.client_name || "—")}
                      <div className="text-[9.5px] text-[#737373] font-mono-tab">#{(it.record_id || "").slice(0, 8)}</div>
                    </td>
                    <td className="py-1.5 px-2 text-[#A3A3A3]">{it.socio_name || <span className="italic text-[#737373]">—</span>}</td>
                    {tab === "ativos" && (
                      <td className="py-1.5 px-2">
                        {(() => {
                          const p = it.probabilidade || "Média";
                          const cls = p === "Alta" ? "border-emerald-500/50 text-emerald-300 bg-emerald-500/10"
                                    : p === "Média" ? "border-amber-500/50 text-amber-300 bg-amber-500/10"
                                    : "border-red-500/50 text-red-300 bg-red-500/10";
                          return <span className={`badge-base text-[10px] ${cls}`} data-testid={`prob-${it.recovery_id}`}>{p}</span>;
                        })()}
                      </td>
                    )}
                    {tab === "ativos" ? (
                      <>
                        <td className="py-1.5 px-2 text-right font-mono-tab text-red-300">{it.dias_atraso} d</td>
                        <td className="py-1.5 px-2 text-right font-mono-tab text-[#A3A3A3]">{it.dias_em_recuperacao} d</td>
                      </>
                    ) : (
                      <td className="py-1.5 px-2 text-[#A3A3A3]">
                        <div className="text-[11px]">{it.exited_at ? new Date(it.exited_at).toLocaleDateString("pt-BR") : "—"}</div>
                        <div className="text-[9.5px] text-[#737373]">{it.dias_em_recuperacao} dias em rec.</div>
                      </td>
                    )}
                    <td className="py-1.5 px-2 text-right font-mono-tab">{formatBRL(it.capital)}</td>
                    <td className="py-1.5 px-2 text-right font-mono-tab text-[#A3A3A3]">{formatBRL(it.juros_em_aberto)}</td>
                    <td className="py-1.5 px-2 text-right font-mono-tab text-amber-300">
                      {formatBRL(tab === "ativos" ? it.saldo_em_aberto : it.total_carteira)}
                    </td>
                    <td className="py-1.5 px-2 text-right font-mono-tab text-emerald-300">{formatBRL(it.total_pago)}</td>
                    <td className="py-1.5 px-2">
                      <span className={`badge-base text-[10px] ${
                        tab === "recuperados"
                          ? (it.exit_reason === "Recuperado"
                              ? "border-emerald-500/30 text-emerald-300 bg-emerald-500/10"
                              : it.exit_reason === "Prejuízo"
                              ? "border-red-500/30 text-red-300 bg-red-500/10"
                              : "border-cyan-500/30 text-cyan-300 bg-cyan-500/10")
                          : "border-amber-500/40 text-amber-200 bg-amber-500/10"
                      }`} title={it.exit_notes || ""}>
                        {it.status_recuperacao}
                      </span>
                    </td>
                    {tab === "ativos" && (
                      <td className="py-1.5 px-2 text-right whitespace-nowrap">
                        <button
                          onClick={() => setWaClient({ id: it.client_id, full_name: it.client_name })}
                          className="text-[10.5px] px-2 py-1 rounded-md border border-emerald-500/40 text-emerald-200 bg-emerald-500/10 hover:bg-emerald-500/20 transition-colors mr-1"
                          data-testid={`btn-wa-${it.recovery_id}`}
                          title="Enviar cobrança via WhatsApp"
                        >
                          <MessageSquare size={10} className="inline mr-1"/>WhatsApp
                        </button>
                        <button
                          onClick={() => setNegociar({
                            client_id: it.client_id,
                            saldo: it.saldo_em_aberto,
                            record_id: it.record_id,
                            client_name: it.client_name,
                          })}
                          className="text-[10.5px] px-2 py-1 rounded-md border border-fuchsia-500/40 text-fuchsia-200 bg-fuchsia-500/10 hover:bg-fuchsia-500/20 transition-colors mr-1"
                          data-testid={`btn-negociar-${it.recovery_id}`}
                          title={`Criar acordo com saldo de ${formatBRL(it.saldo_em_aberto)}`}
                        >
                          <Handshake size={10} className="inline mr-1"/>Negociar
                        </button>
                        {isAdmin && (
                          <button
                            onClick={() => setExitRow(it)}
                            className="text-[10.5px] px-2 py-1 rounded-md border border-[#1f1f1f] text-[#A3A3A3] hover:bg-[#0f0f0f] transition-colors"
                            data-testid={`btn-mover-${it.recovery_id}`}
                            title="Mover para fora da recuperação"
                          >
                            <ArrowRightLeft size={10} className="inline mr-1"/>Mover
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      </>
      )}

      {exitRow && (
        <ExitModal
          row={exitRow}
          onClose={() => setExitRow(null)}
          onDone={() => { setExitRow(null); load(); }}
        />
      )}

      {negociar && (
        <NewInstallmentContractModal
          onClose={() => setNegociar(null)}
          onCreated={() => { setNegociar(null); toast.success("Acordo criado. O registro original permanecerá em recuperação até quitação."); load(); }}
          prefilledClientId={negociar.client_id}
          prefilledKind="acordo"
          prefilledAgreedAmount={negociar.saldo}
          prefilledAcordoCapitalBase={negociar.saldo}
          prefilledAgreementReason={`Negociação de saldo em recuperação — contrato #${negociar.record_id.slice(0,8)}`}
        />
      )}

      <SendWhatsAppModal
        open={!!waClient}
        onClose={() => setWaClient(null)}
        clients={waClient ? [waClient] : []}
        defaultCategory="recuperacao"
      />
    </Layout>
  );
}
